/**
 * Geocoding utilities using OpenStreetMap Nominatim API
 * https://nominatim.org/release-docs/develop/api/Overview/
 */

export interface Coordinates {
  lat: number;
  lon: number;
}

export interface GeocodingResult {
  coordinates: Coordinates;
  displayName: string;
}

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Extract department code from French postal code
 * French postal codes: first 2 digits = department (e.g., 75001 -> 75, 33000 -> 33)
 */
function getDepartmentFromPostalCode(postalCode: string | undefined): string | undefined {
  if (!postalCode) return undefined;
  const match = postalCode.match(/^(\d{2})/);
  return match ? match[1] : undefined;
}

/**
 * Geocode a location using OpenStreetMap Nominatim
 */
async function geocode(query: string, country?: string): Promise<GeocodingResult | null> {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: '1',
    addressdetails: '1',
  });

  if (country) {
    params.append('countrycodes', country.toLowerCase());
  }

  try {
    const response = await fetch(`${NOMINATIM_BASE_URL}?${params.toString()}`, {
      headers: {
        'User-Agent': 'BellesDemeures/1.0', // Required by Nominatim
      },
    });

    if (!response.ok) {
      console.error('Geocoding failed:', response.statusText);
      return null;
    }

    const data = await response.json();
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    const result = data[0];
    return {
      coordinates: {
        lat: parseFloat(result.lat),
        lon: parseFloat(result.lon),
      },
      displayName: result.display_name,
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

/**
 * Get coordinates for a city
 */
export async function geocodeCity(
  city: string | undefined,
  postalCode: string | undefined,
  country: string | undefined
): Promise<GeocodingResult | null> {
  if (!(city || postalCode)) return null;

  const query = postalCode && city ? `${postalCode} ${city}` : city || postalCode || '';
  return geocode(query, country);
}

/**
 * Get coordinates for a department (France) or country
 */
export async function geocodeDepartmentOrCountry(
  postalCode: string | undefined,
  country: string | undefined
): Promise<GeocodingResult | null> {
  const isFrance = !country || country.toLowerCase() === 'france' || country.toLowerCase() === 'fr';

  if (isFrance && postalCode) {
    const departmentCode = getDepartmentFromPostalCode(postalCode);
    if (departmentCode) {
      // Geocode the department center
      return geocode(`Département ${departmentCode}, France`, 'fr');
    }
  }

  // If not France or no postal code, geocode the country
  if (country) {
    return geocode(country);
  }

  return null;
}

/**
 * Get coordinates for a country
 */
export async function geocodeCountry(country: string | undefined): Promise<GeocodingResult | null> {
  if (!country) return null;
  return geocode(country);
}
